import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import { cn } from '@/utils/cn'
import type { AdminApplicationItem } from '@/types/controlCenter'
import { QUERY_KEYS } from '../utils/queryKeys'
import { labelClass, inputClass } from '../components/shared/modalStyles'
import ModalShell from '../components/shared/ModalShell'

interface ApplicationModalProps {
  app?: AdminApplicationItem | null
  onClose: () => void
}

const ApplicationModal = ({ app, onClose }: ApplicationModalProps) => {
  const queryClient = useQueryClient()
  const isCreate = !app

  const [name, setName] = useState(app?.name ?? '')
  const [description, setDescription] = useState(app?.description ?? '')
  const [key, setKey] = useState(app?.key ?? '')
  const [imageUrl, setImageUrl] = useState(app?.image_url ?? '')
  const [clientSupported, setClientSupported] = useState(
    app?.client_supported ?? false,
  )

  const mutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.applications,
      })
      onClose()
    },
  })

  const handleSave = () => {
    mutation.mutate({
      method: isCreate ? 'post' : 'put',
      url: isCreate
        ? `${CONTROL_CENTRE_API_DOMAIN}/admin/applications`
        : `${CONTROL_CENTRE_API_DOMAIN}/admin/applications/${app.id}`,
      data: {
        name: name.trim(),
        description: description.trim(),
        key: key.trim(),
        image_url: imageUrl.trim(),
        client_supported: clientSupported,
      },
    })
  }

  return (
    <ModalShell
      title={isCreate ? 'Create Application' : 'Edit Application'}
      subtitle={!isCreate ? app.name : undefined}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
        className='max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5'
      >
        <div>
          <label htmlFor='app-name' className={labelClass}>
            Name *
          </label>
          <input
            id='app-name'
            required
            aria-required='true'
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Application name'
          />
        </div>
        <div>
          <label htmlFor='app-description' className={labelClass}>
            Description
          </label>
          <textarea
            id='app-description'
            className={cn(inputClass, 'resize-none')}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Brief description of this application'
          />
        </div>
        <div>
          <label htmlFor='app-key' className={labelClass}>
            Key *
          </label>
          <input
            id='app-key'
            required
            aria-required='true'
            className={inputClass}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder='e.g. hubble-insights'
          />
        </div>
        <div>
          <label htmlFor='app-image-url' className={labelClass}>
            Image URL
          </label>
          <input
            id='app-image-url'
            className={inputClass}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder='https://…'
          />
        </div>
        <div className='border-border-main bg-neutral-dimmed flex items-center justify-between rounded-lg border px-4 py-3'>
          <div>
            <p className={labelClass}>Client Supported</p>
            <p className='text-text-tertiary text-xs'>
              Enable client-level targeting for alerts using this application
            </p>
          </div>
          <button
            type='button'
            role='switch'
            aria-checked={clientSupported}
            aria-label='Toggle client supported'
            onClick={() => setClientSupported((v) => !v)}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
              clientSupported ? 'bg-violet-600' : 'bg-neutral-dimmed-heavy',
            )}
          >
            <span
              className={cn(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                clientSupported ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
        </div>

        {mutation.error && (
          <p className='text-sm text-red-400'>{mutation.error.message}</p>
        )}

        <div className='border-border-main flex items-center justify-end gap-3 border-t px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            className='border-border-main text-text-secondary hover:bg-neutral-dimmed rounded-lg border px-4 py-2 text-sm'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={
              name.trim() === '' || key.trim() === '' || mutation.isPending
            }
            className='rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40'
          >
            {mutation.isPending
              ? 'Saving…'
              : isCreate
                ? 'Create Application'
                : 'Save'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

export default ApplicationModal
